import { Component, Input, HostBinding, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef, Inject, EventEmitter, Output } from '@angular/core'
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl, Validators } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { from, noop, OperatorFunction, Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, mergeMap } from 'rxjs/operators'
import { BootstrapData, CustomField, CustomFieldFromJSON, CustomFieldValidationEnum } from 'elements-sdk'

import { MainConstants } from 'main/constants'
import { BOOTSTRAP_DATA } from 'api/providers'
import { BaseComponent, isValidRegex } from 'common/helpers'
import { CustomFieldsService } from '../services/customFields.service'
import { NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import { NgbDatepickerAdapter, NgbDatepickerParserFormatter } from 'media.customFields/services/ngBootstrap.service'

@Component({
  selector: 'custom-field-input',
  templateUrl: './customFieldInput.component.pug',
  styleUrls: ['./customFieldInput.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomFieldInputComponent),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbDatepickerParserFormatter },
    { provide: NgbDateAdapter, useClass: NgbDatepickerAdapter },
  ],
  host: {
    '[class.input-group]': 'true',
  },
})
export class CustomFieldInputComponent extends BaseComponent implements ControlValueAccessor {
  @Input() field: CustomField
  @Output() enter = new EventEmitter<void>()
  @Input() placeholder = ''
  @Input() indeterminate = false
  @Input() dynamic = false
  @Input() readonly = false
  @Output() focus = new EventEmitter<void>()
  @Output() blur = new EventEmitter<void>()
  @Input() datepickerContainer?: string

  formControl = new FormControl(undefined, { updateOn: 'change' })

  innerModel: any
  innerModelChange = new Subject<any>()
  datepickerValue: NgbDateStruct
  disabled = false

  options: string[] = []
  datepickerOptions: any = {
    autoclose: true,
    container: null,
  }
  @HostBinding('class.dynamic-mode') dynamicMode = false
  knownUsernames: string[]

  constructor (
    private translate: TranslateService,
    private customFields: CustomFieldsService,
    private cdr: ChangeDetectorRef,
    private mainConstants: MainConstants,
    @Inject(BOOTSTRAP_DATA) bootstrapData: BootstrapData,
  ) {
    super()
    // TODO uncomment when no NG1 consumers are left
    // @Self() @Optional() private control?: NgControl,
    // if (this.control) {
    //   this.control.valueAccessor = this
    // }
    this.knownUsernames = bootstrapData.knownUsernames

    const debounce = window['__karma__'] ? 10 : 100
    this.innerModelChange.pipe(debounceTime(debounce), distinctUntilChanged()).subscribe(() => {
      this.dynamicMode = this.innerModel && this.mainConstants.validationPatterns.variablesPresent.test(this.innerModel.toString())
      if (this.formControl.valid || this.dynamicMode) {
        this.emitModelChange(this.innerModel)
      }
    })
  }

  ngAfterViewInit () {
    this.subs.add(
      this.formControl.valueChanges.subscribe(v => {
        this.innerModel = v
        this.innerModelChange.next(v)
        this.cdr.markForCheck()
      }),
    )
  }

  ngOnChanges (changes) {
    if ('number_of_digits' in this.field) {
      this.field = CustomFieldFromJSON(this.field)
    }

    this.datepickerOptions.container = this.datepickerContainer ?? '.layout-view'
    if (changes.field) {
      if (!this.field.type) {
        return
      }
      this.formControl.clearValidators()
      if (this.field.validation === CustomFieldValidationEnum.Range) {
        // eslint-disable-next-line eqeqeq
        if (this.field.rangeMin != undefined) {
          this.formControl.addValidators(Validators.min(this.field.rangeMin))
        }
        // eslint-disable-next-line eqeqeq
        if (this.field.rangeMax != undefined) {
          this.formControl.addValidators(Validators.max(this.field.rangeMax))
        }
      }
      if (this.field.type === 'select') {
        this.options = this.field.options
        this.coerceMultiselectType()
      } else if (this.field.type === 'user') {
        this.options = this.knownUsernames
      } else if (this.field.type === 'url') {
        this.formControl.addValidators(Validators.pattern('^(http[s]?:\\/\\/(www\\.)?|ftp:\\/\\/(www\\.)?|www\\.){1}([0-9A-Za-z-\\.@:%_\\+~#=]+)+((\\.[a-zA-Z]{2,3})+)(\\/(.)*)?(\\?(.)*)?'))
      }
      if (this.field.validation === CustomFieldValidationEnum.NumberOfDigits) {
        this.formControl.addValidators(Validators.pattern(`\\d{${this.field.numberOfDigits}}`))
      }
      if (this.field.validation === CustomFieldValidationEnum.Regex && this.field.regex && isValidRegex(this.field.regex)) {
        this.formControl.addValidators(Validators.pattern(this.field.regex))
      }
    }

    if (this.disabled || this.readonly || this.field.nonUserEditable) {
      this.formControl.disable()
    } else {
      this.formControl.enable()
    }
  }

  ngOnDestroy () {
    this.innerModelChange.complete()
  }

  isValidOption () {
    if (this.field.type !== 'select') {
      return true
    }
    return this.field.options.includes(this.innerModel)
  }

  async getAutocompletions (query) {
    return this.customFields.getAutocompletions(this.field, query)
  }

  getPlaceholder () {
    return this.indeterminate ? this.translate.instant('Multiple values') : this.field.helpText ?? this.placeholder
  }

  coerceMultiselectType () {
    if (this.field.multipleResponse) {
      if (!this.innerModel) {
        this.emitModelChange([])
      }
      if (typeof this.innerModel === 'string') {
        this.emitModelChange(this.innerModel.split(', ').filter(x => x))
      }
    }
    if (!this.field.multipleResponse && typeof this.innerModel === 'object') {
      this.innerModel ??= []
      this.emitModelChange(this.innerModel.filter(x => x).join(', '))
    }
  }

  getMultiselectLabel () {
    if (typeof this.innerModel === 'string') {
      return this.innerModel
    }
    return this.innerModel?.length ? this.innerModel.join(', ') : this.translate.instant('None selected')
  }

  toggleMultiselectOption (option) {
    this.coerceMultiselectType()
    if (typeof this.innerModel === 'string') {
      this.innerModel = this.innerModel.split(', ')
    }
    if (this.innerModel.includes(option)) {
      this.innerModel = this.innerModel.filter(x => x !== option)
    } else {
      this.innerModel = [...this.innerModel, option]
    }
    this.emitModelChange(this.innerModel)
  }

  isMultiselectOptionSelected (option) {
    return this.innerModel?.includes(option)
  }

  getTooltip () {
    let tooltip = ''
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (this.field.validation) {
      case CustomFieldValidationEnum.NumberOfDigits:
        tooltip = `${this.translate.instant('Expected digits:')} ${this.field.numberOfDigits ?? this.field.numberOfDigits}`
        break
      case CustomFieldValidationEnum.Regex:
        tooltip = `${this.translate.instant('Regular expression:')} ${this.field.regex}`
        break
    }
    return (tooltip + '\n' + (this.field.helpText ?? '')).trim()
  }

  getEffectiveType () {
    if (this.dynamicMode) {
      return 'text'
    }
    return this.field.type
  }

  isDynamicModeAllowed () {
    return this.dynamic && !['text', 'large-text', 'url', 'path'].includes(this.field.type)
  }

  emitModelChange (value: any) {
    this.innerModel = value
    this.onChangeHandler(value)
  }

  autocomplete: OperatorFunction<string, readonly string[]> = text$ => {
    const debounce = window['__karma__'] ? 10 : 200
    return text$.pipe(
      debounceTime(debounce),
      distinctUntilChanged(),
      mergeMap(term => !term ? [] : from(this.getAutocompletions(term))),
      map(result => result.slice(0, 10)),
    )
  }

  get controlDisabled (): boolean {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return this.field.nonUserEditable || this.disabled || this.readonly
  }

  // ControlValueAccessor
  private onChangeHandler: CallableFunction = noop
  private onTouchHandler: CallableFunction = noop

  writeValue (value: any) {
    if (this.innerModel !== value) {
      this.cdr.markForCheck()
    }
    this.innerModel = value
    this.formControl.setValue(this.innerModel)
  }

  registerOnChange (fn) {
    this.onChangeHandler = fn
  }

  registerOnTouched (fn) {
    this.onTouchHandler = fn
  }

  setDisabledState (disabled) {
    this.disabled = disabled
    this.cdr.markForCheck()
  }

  // eslint-disable-next-line accessor-pairs
  @Input('disabled') set disabledAttribute (value) {
    this.disabled = value
  }
}
